import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, RefreshCw, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);

  // Convert modal
  const [showConvert, setShowConvert] = useState(false);
  const [convertData, setConvertData] = useState({ bill_number: '', bill_date: '', payment_term_id: '', paid_amount: '' });
  const [convertLineWarranties, setConvertLineWarranties] = useState([]);
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes, ptRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/purchase-orders/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/payment-terms`, getAuthHeaders()),
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
        setPaymentTerms(ptRes.data);
      } catch (err) { setError('Failed to load purchase order'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const getName = (list, id, field = 'name') => list.find(i => i.id === id)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const openConvertModal = () => {
    setConvertData({
      bill_number: '',
      bill_date: new Date().toISOString().split('T')[0],
      payment_term_id: '',
      paid_amount: '',
    });
    setConvertLineWarranties((doc.line_items || []).map(() => ''));
    setConvertError('');
    setShowConvert(true);
  };

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    setConvertError('');
    if (!convertData.bill_number.trim()) { setConvertError('Bill Number is required'); return; }
    setConvertSubmitting(true);
    try {
      const paidAmt = parseFloat(convertData.paid_amount) || 0;
      const payload = {
        vendor_id: doc.vendor_id,
        bill_number: convertData.bill_number,
        bill_date: convertData.bill_date || null,
        state_of_supply: doc.state_of_supply,
        company_id: doc.company_id,
        payment_term_id: convertData.payment_term_id || null,
        line_items: (doc.line_items || []).map((li, idx) => ({
          product_id: li.product_id,
          description: li.description,
          mrp: li.mrp,
          quantity: li.quantity,
          unit_id: li.unit_id,
          price_per_unit: li.price_per_unit,
          discount_percentage: li.discount_percentage,
          discount_amount: li.discount_amount,
          tax_id: li.tax_id,
          tax_amount: li.tax_amount,
          total_amount: li.total_amount,
          warranty_days: parseInt(convertLineWarranties[idx]) || null,
        })),
        payments: (doc.payments || []).map(p => ({ payment_type: p.payment_type, amount: p.amount })),
        description: doc.description,
        round_off: doc.round_off,
        round_off_amount: doc.round_off_amount,
        subtotal: doc.subtotal,
        total: doc.total,
        paid_amount: paidAmt,
        balance: (doc.total || 0) - paidAmt,
      };
      const res = await axios.post(`${BACKEND_URL}/api/purchase-bills`, payload, getAuthHeaders());
      navigate(`/admin/purchase/bills/${res.data.id}`);
    } catch (err) { setConvertError(err.response?.data?.detail || 'Failed to create purchase bill'); }
    finally { setConvertSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;

  return (
    <div data-testid="purchase-order-detail-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <button onClick={() => navigate('/admin/purchase/orders')} data-testid="back-btn"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-3 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Purchase Orders
          </button>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Purchase Order Details</h1>
        </div>
        <button onClick={openConvertModal} data-testid="convert-to-bill-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <RefreshCw className="h-5 w-5" /> Convert to Purchase Bill
        </button>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Order Number</span><span className="text-sm font-semibold text-slate-900">{doc.order_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Order Date</span><span className="text-sm text-slate-900">{formatDate(doc.order_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Due Date</span><span className="text-sm text-slate-900">{formatDate(doc.due_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Vendor</span><span className="text-sm text-slate-900">{doc.vendor_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Company</span><span className="text-sm text-slate-900">{doc.company_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">State of Supply</span><span className="text-sm text-slate-900">{doc.state_of_supply || '-'}</span></div>
          {doc.description && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Description</span><span className="text-sm text-slate-900">{doc.description}</span></div>}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">MRP</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-right">Disc Amt</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{li.description || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.mrp || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.quantity}</td>
                  <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                  <td className="px-4 py-3 text-right">{li.price_per_unit?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{li.discount_percentage}%</td>
                  <td className="px-4 py-3 text-right">{li.discount_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                  <td className="px-4 py-3 text-right">{li.tax_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">{li.total_amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span></div>
          {doc.round_off && <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{(doc.total || 0).toLocaleString()}</span></div>
        </div>
      </div>

      {/* Convert to Purchase Bill Modal */}
      {showConvert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Convert to Purchase Bill</h2>
              <button onClick={() => setShowConvert(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleConvertSubmit} className="p-6">
              {convertError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6"><AlertCircle className="h-5 w-5" /><span>{convertError}</span></div>}

              {/* Info banner */}
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-sm mb-6 text-sm">
                The following details from the Purchase Order will be carried over: Vendor, Company, State of Supply, Line Items, Payments, Description, and Totals. Please fill in the additional fields below.
              </div>

              {/* Bill Header Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bill Number <span className="text-red-500">*</span></label>
                  <input type="text" value={convertData.bill_number}
                    onChange={e => setConvertData(prev => ({ ...prev, bill_number: e.target.value }))}
                    data-testid="convert-bill-number" placeholder="e.g. PB-001"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bill Date</label>
                  <input type="date" value={convertData.bill_date}
                    onChange={e => setConvertData(prev => ({ ...prev, bill_date: e.target.value }))}
                    data-testid="convert-bill-date"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Term</label>
                  <select value={convertData.payment_term_id}
                    onChange={e => setConvertData(prev => ({ ...prev, payment_term_id: e.target.value }))}
                    data-testid="convert-payment-term"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">-- Select Payment Term --</option>
                    {paymentTerms.filter(pt => pt.active).map(pt => (
                      <option key={pt.id} value={pt.id}>{pt.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Paid Amount */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Paid Amount</label>
                  <input type="number" value={convertData.paid_amount}
                    onChange={e => setConvertData(prev => ({ ...prev, paid_amount: e.target.value }))}
                    data-testid="convert-paid-amount" min="0" step="0.01" placeholder="0"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="flex items-end">
                  <div className="text-sm text-slate-600">
                    <span className="block text-xs text-slate-500 uppercase mb-1">Total from PO</span>
                    <span className="font-bold text-slate-900">₹{(doc.total || 0).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex items-end">
                  <div className="text-sm text-slate-600">
                    <span className="block text-xs text-slate-500 uppercase mb-1">Balance</span>
                    <span className="font-bold text-slate-900">₹{((doc.total || 0) - (parseFloat(convertData.paid_amount) || 0)).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Warranty per line item */}
              {(doc.line_items || []).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">Warranty per Line Item</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Product</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-right w-32">Warranty (Days)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(doc.line_items || []).map((li, idx) => (
                          <tr key={idx} className="border-b border-slate-100">
                            <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                            <td className="px-3 py-2 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                            <td className="px-3 py-2 text-right">{li.quantity}</td>
                            <td className="px-3 py-2 text-right">{li.total_amount?.toFixed(2)}</td>
                            <td className="px-3 py-2">
                              <input type="number" value={convertLineWarranties[idx] || ''}
                                onChange={e => {
                                  const newW = [...convertLineWarranties];
                                  newW[idx] = e.target.value;
                                  setConvertLineWarranties(newW);
                                }}
                                data-testid={`convert-warranty-${idx}`} min="0" placeholder="0"
                                className="w-full px-2 py-1 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-right text-sm" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowConvert(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={convertSubmitting} data-testid="confirm-convert-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {convertSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Convert & Create Bill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
